// lib/coupons/requestLines.ts
//
// A1b (TABC): las rutas de cupón PRE-cobro (/api/coupons/apply, /validate y
// /api/online-orders/coupon-preview) reciben, además del `subtotal`, las líneas
// del carrito para medir la base del cupón SIN alcohol. Este módulo es la ÚNICA
// definición de ese contrato: el esquema del body y la cuenta.
//
// 🟡 Es VISTA/ancla, no cobro: `subtotal` y `line_subtotal` vienen del cliente.
// El número que se cobra lo decide /api/orders (caja y kiosko, desde el
// snapshot del ancla y los precios de la BD) o /session (online). Una línea
// mentida solo se miente a sí misma.

import { z } from "zod"
import { couponBaseFromLines, lineIsCouponAlcohol, type CouponAlcoholFacts } from "@/lib/coupons/alcoholExclusion"
import { cartItemLineTotal } from "@/lib/utils/pricing"
import type { CartItem } from "@/types/pos"

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const MAX_REQUEST_LINES = 50
const MAX_OPTION_IDS = 200

const uuid = z.string().regex(UUID_RE, "Invalid UUID")

/**
 * `lines?` del body. Opcional: un cliente que no lo manda (todos hoy) recibe la
 * respuesta de siempre. Tope 50 = el de `items` en /api/orders.
 * - `item_id`: null en un ítem personalizado (solo lo deciden sus opciones).
 * - `option_ids`: las opciones de modificador de la línea.
 * - `line_subtotal`: lo que la línea aporta al `subtotal` (la MISMA expresión).
 */
export const couponRequestLinesSchema = z
  .array(
    z.object({
      item_id: uuid.nullable(),
      option_ids: z.array(uuid).max(MAX_OPTION_IDS),
      line_subtotal: z.number().finite(),
    }),
  )
  .max(MAX_REQUEST_LINES)
  .optional()

export type CouponRequestLines = z.infer<typeof couponRequestLinesSchema>

/**
 * Base del cupón a partir de las líneas del body. Sin líneas (o sin alcohol en
 * ellas) devuelve `subtotal` intacto y `excluded: false`: el llamador pasa
 * entonces la llamada de main a validateCouponStrict.
 *
 * Una línea negativa se cuenta como 0: restarla SUBIRÍA la base por encima del
 * subtotal (más descuento del que corresponde).
 */
export function couponBaseFromRequestLines(
  subtotal: number,
  lines: CouponRequestLines,
  facts: CouponAlcoholFacts,
): { couponBase: number; excluded: boolean } {
  if (!lines || lines.length === 0) return { couponBase: subtotal, excluded: false }
  return couponBaseFromLines(
    subtotal,
    lines.map((l) => ({
      lineSubtotal: Math.max(0, l.line_subtotal),
      isAlcohol: lineIsCouponAlcohol({ item_id: l.item_id, optionIds: l.option_ids }, facts),
    })),
  )
}

/** Las listas del local tal como viajan en la respuesta (copias, nunca el objeto congelado). */
export function couponAlcoholResponseFields(facts: CouponAlcoholFacts): {
  alcoholItemIds: string[]
  alcoholOptionIds: string[]
} {
  return { alcoholItemIds: [...facts.alcoholItemIds], alcoholOptionIds: [...facts.alcoholOptionIds] }
}

// ─── Lado CLIENTE: armar `lines` para el body ────────────────────────────────

/**
 * Deja `lines` listas para el esquema de la ruta, reparando lo reparable en vez
 * de tirar todo el carrito por una sola línea rara.
 *
 * Un id que no es uuid (p. ej. la opción sintética de un combo, o un ítem
 * personalizado con id propio) NUNCA puede ser alcohol: las listas del local son
 * uuids de la BD. Por eso, línea por línea:
 * - `item_id` que no es uuid ⇒ `null` (la línea solo la deciden sus opciones);
 * - `option_ids` que no son uuid ⇒ se descartan, las demás se quedan.
 * Así una opción sintética en una línea ya no le quita la exclusión de alcohol
 * a las otras líneas del carrito.
 *
 * Lo que NO se puede reparar devuelve `undefined` y el cliente NO manda `lines`
 * (la ruta responde como en main, base = subtotal, en vez de un 400 que tumbaría
 * un cupón bueno): carrito vacío, más de 50 líneas, un `line_subtotal` no finito
 * o más de 200 opciones en una línea. Lo que se cobra lo sigue decidiendo
 * /api/orders o /session, con el alcohol excluido.
 */
export function sanitizeCouponRequestLines(
  lines: ReadonlyArray<{ item_id: string | null; option_ids: readonly string[]; line_subtotal: number }>,
): CouponRequestLines {
  if (lines.length === 0 || lines.length > MAX_REQUEST_LINES) return undefined
  const repaired: NonNullable<CouponRequestLines> = []
  for (const l of lines) {
    if (!Number.isFinite(l.line_subtotal)) return undefined
    const option_ids = l.option_ids.filter((id) => UUID_RE.test(id))
    if (option_ids.length > MAX_OPTION_IDS) return undefined
    repaired.push({
      item_id: l.item_id != null && UUID_RE.test(l.item_id) ? l.item_id : null,
      option_ids,
      line_subtotal: l.line_subtotal,
    })
  }
  // Red de seguridad: lo que viaja pasó por el MISMO esquema que valida la ruta.
  const parsed = couponRequestLinesSchema.safeParse(repaired)
  return parsed.success ? parsed.data : undefined
}

/**
 * `lines` de la caja y del kiosko. Recibe los ítems YA efectivos
 * (effectiveCartItems con el mismo método de pago que el `subtotal` que se
 * manda): `line_subtotal` es cartItemLineTotal sin cfg — la misma expresión con
 * la que calculateOrderTotals suma su rawSubtotal —, así que Σ line_subtotal es
 * ese `subtotal`.
 */
export function couponRequestLines(effItems: ReadonlyArray<CartItem>): CouponRequestLines {
  return sanitizeCouponRequestLines(
    effItems.map((i) => ({
      item_id: i.item_id,
      option_ids: i.modifiers.map((m) => m.modifier_option_id),
      line_subtotal: cartItemLineTotal(i),
    })),
  )
}
